import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import type {
  BidShowcaseItem,
  ShowcaseFileThumbnail,
  ShowcaseItem,
} from '@freelancer/datastore/collections';
import { isImageFile, isPlayableVideoFile } from '@freelancer/ui';
import { FileDisplayType } from '@freelancer/ui/file-display';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureLayout, PictureObjectFit } from '@freelancer/ui/picture';

/**
 * A minimal Showcase object that only contains the necessary fields for extracting filename and thumbnail
 *
 * This is a subset of Showcase
 */
interface MinimalShowcase {
  // Made this optional because Showcase.coverItem is optional, just to make Showcase compatible with this type
  // But undefined coverItem should be impossible (see showcase.transformers.ts)
  coverItem?: MinimalShowcaseItem;
}

type MinimalShowcaseItem = Pick<ShowcaseItem, 'itemFile'>;

type MinimalBidShowcaseItem = Pick<BidShowcaseItem, 'filename' | 'thumbnail'>;

@Component({
  standalone: false,
  selector: 'fl-showcase-thumbnail',
  template: `
    @if (thumbnail && thumbnail.cdnUrl && !thumbnailImageError) {
      <div class="ShowcaseThumbnail">
        <fl-picture
          class="ShowcaseThumbnail-image"
          [alignCenter]="true"
          [alt]="filename"
          [aspectRatioValue]="'rectangle-small'"
          [borderRadiusBottom]="true"
          [borderRadiusTop]="true"
          [fixedAspectRatio]="true"
          [layout]="PictureLayout.FILL"
          [objectFit]="PictureObjectFit.COVER"
          [src]="thumbnail.cdnUrl"
          [width]="SHOWCASE_THUMBNAIL_WIDTH"
          (imageLoaded)="thumbnailImageLoading = false"
          (onError)="thumbnailImageError = true"
        >
        </fl-picture>
        @if (thumbnailImageLoading) {
          <fl-loading-template class="ShowcaseThumbnail-loading" />
        }
        @if (isFileVideoPlayable && !thumbnailImageLoading) {
          <div class="ShowcaseThumbnail-playButton">
            <fl-icon
              [size]="IconSize.LARGE"
              [color]="IconColor.LIGHT"
              [name]="'ui-play'"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          </div>
        }
      </div>
    } @else {
      <div class="ShowcaseThumbnail-default">
        <fl-file-display
          class="ShowcaseThumbnail-default-icon"
          [alt]="filename"
          [src]="filename"
          [iconColor]="IconColor.LIGHT"
          [iconSize]="IconSize.MID"
          [type]="FileDisplayType.ICON"
        ></fl-file-display>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./showcase-thumbnail.component.scss'],
})
export class ShowcaseThumbnailComponent implements OnChanges {
  IconColor = IconColor;
  IconSize = IconSize;
  FileDisplayType = FileDisplayType;
  PictureLayout = PictureLayout;
  PictureObjectFit = PictureObjectFit;

  /**
   * In all usage of this component, height is explicitly set to 62px in the scss file
   * with an aspect ratio of 3:2. With that, the maximum width is 93px (62px * 3/2).
   */
  readonly SHOWCASE_THUMBNAIL_WIDTH = 93;

  /**
   * Either a (minimal) Showcase or a (minimal) BidShowcaseItem must be provided to this component.
   *
   * The types are minimal version to not force the consumer of this component to provide the whole `Showcase` or `BidShowcaseItem` object,
   * only the necessary fields required to display the thumbnail.
   */
  @Input() showcase: MinimalShowcase | MinimalBidShowcaseItem;

  filename: string;
  isFileVideoPlayable = false;
  thumbnail?: ShowcaseFileThumbnail;
  thumbnailImageError = false;
  thumbnailImageLoading = true;

  ngOnChanges(changes: SimpleChanges): void {
    if ('showcase' in changes && this.showcase) {
      if ('coverItem' in this.showcase) {
        // `this.showcase` is of type MinimalShowcase here
        this.filename = this.getShowcaseFilename(this.showcase);
        this.thumbnail = this.getShowcaseThumbnail(this.showcase);
        this.isFileVideoPlayable = isPlayableVideoFile(this.filename);
      } else if ('filename' in this.showcase && 'thumbnail' in this.showcase) {
        // `this.showcase` is of type MinimalBidShowcaseItem here
        this.filename = this.showcase.filename;
        this.thumbnail = this.showcase.thumbnail;
        this.isFileVideoPlayable = isPlayableVideoFile(this.filename);
      }
    }
  }

  getShowcaseCoverItem(showcase: MinimalShowcase): MinimalShowcaseItem {
    const { coverItem } = showcase;

    // This case should be impossible (see showcase.transformers.ts), but we need to make the typescript happy
    if (coverItem === undefined) {
      throw new ReferenceError("Showcase's cover item is undefined");
    }

    return coverItem;
  }

  getShowcaseFilename(showcase: MinimalShowcase): string {
    const coverItem = this.getShowcaseCoverItem(showcase);

    return coverItem.itemFile.filename;
  }

  getShowcaseThumbnail(
    showcase: MinimalShowcase,
  ): ShowcaseFileThumbnail | undefined {
    const { itemFile } = this.getShowcaseCoverItem(showcase);
    const { filename } = itemFile;

    // If the filename is an image, it can self-represent its own thumbnail
    // Prioritize the original image over its thumbnails, since we are using fastly anyways
    if (isImageFile(filename)) {
      return {
        filename,
        fileserviceFileId: itemFile.fileserviceFileId,
        cdnUrl: itemFile.url,
      };
    }

    const thumbnails = Object.values(itemFile.thumbnails);

    // Try to return a thumbnail from itemFile.thumbnails if available
    // Largest thumbnail is placed at the end of itemFile.thumbnails object
    return thumbnails.at(-1);
  }
}
